import React, { useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useQueryParam from '@jwp/ott-ui-react/src/hooks/useQueryParam';
import { useQuery } from '@apollo/client/react';
import { useUploadStore } from '@jwp/ott-common/src/stores/UploadStore';
import { getAuthApolloClient } from '@jwp/ott-common/src/graphql/ApolloClient';
import { GET_MY_VIDEOS, GetMyVideosData, GetMyVideosVariables, MediaCategoryReels } from '@jwp/ott-common/src/graphql/query/reels.queries';
import { GET_DROPDOWNS, GetDropdownsVariables, GetDropdownsData } from '@jwp/ott-common/src/graphql/query/broadcastLive.queries';
import env from '@jwp/ott-common/src/env';

import LoadingOverlay from '../../components/LoadingOverlay/LoadingOverlay';
import VideoPreviewSingle from '../../components/VideoPreview/VideoPreviewSingle';

const VideoPreviewSinglePage = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { file, previewUrl, values, videoValues, returnPath } = useUploadStore();
  const urlReturnPath = useQueryParam('returnPath');
  const typeParam = useQueryParam('type');
  const effectiveReturnPath = returnPath || urlReturnPath;


  const { data: dropdownData } = useQuery<GetDropdownsData, GetDropdownsVariables>(GET_DROPDOWNS, {
    client: getAuthApolloClient(),
    variables: { ddType: 'category' },
    errorPolicy: 'all',
  });

  const categories: MediaCategoryReels[] = useMemo(() => dropdownData?.getDropdowns?.dropdowns || [], [dropdownData]);

  const queryVariables = useMemo(
    () => ({
      filter: { maId: { equalTo: id || '' } },
      first: 1,
      offset: 0,
    }),
    [id],
  );


  const { data, loading } = useQuery<GetMyVideosData, GetMyVideosVariables>(GET_MY_VIDEOS, {
    variables: queryVariables,
    skip: !id,
  });

  const videoData = data?.allMediaApprovals?.nodes?.[0];

  const isVideoType = typeParam === 'video';

  const effectiveTitle = useMemo(() => {
    if (id && videoData) { return videoData.maTitle; }
    return isVideoType ? videoValues.title : values.title;
  }, [id, videoData, isVideoType, videoValues.title, values.title]);

  const effectiveDescription = useMemo(() => {
    if (id && videoData) { return videoData.maDescription; }
    return isVideoType ? videoValues.description : values.description;
  }, [id, videoData, isVideoType, videoValues.description, values.description]);

  const handleCategory = (categoryId: string) =>
    categories.find((item) => item.ddId === categoryId)?.ddLabel;

  const effectiveCategory = useMemo(() => {
    if (id && videoData) {
      return handleCategory(videoData.maCategory);
    }

    return isVideoType ? undefined : handleCategory(values.category);
  }, [id, videoData, isVideoType, values.category, categories]);

  const effectiveAuthor = useMemo(() => {
    if (id && videoData) {
      return {
        name: videoData.userByMaUploadedBy?.uFullName || null,
        avatarUrl: videoData.userByMaUploadedBy?.uAvatarUrl || null,
      };
    }
    return undefined;
  }, [id, videoData]);

  const effectiveItem = useMemo(() => {
    if (id && videoData) { return videoData as any; }

    // Minimal item shape for preview/new uploads so UI components that read
    // item fields (e.g. CollapsibleText) don't crash when item is missing.
    return {
      audio_language: undefined,
      sub_title: undefined,
      cast: undefined,
      director: undefined,
      producer: undefined,
      studio: undefined,
      // include title/description for any callers that might read them
      title: isVideoType ? videoValues.title : values.title,
      description: isVideoType ? videoValues.description : values.description,
    } as any;
  }, [id, videoData, isVideoType, videoValues.title, videoValues.description, values.title, values.description]);

  const isReel = videoData ? videoData.maType === 'REELS' : typeParam !== 'video';

  useEffect(() => {
    // If not viewing an existing video (no id param) and no file/previewUrl in memory, redirect.
    if (!id && !file && !previewUrl) {
      const fallbackPath = effectiveReturnPath || `${-1}`;
      navigate(fallbackPath, { replace: true });
    }
  }, [id, file, previewUrl, navigate, effectiveReturnPath]);

  if (!id && !file && !previewUrl) {
    return null;
  }

  if (loading) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#000' }}>
        <LoadingOverlay inline />
      </div>
    );
  }

  const effectiveFile = id ? null : file;
  const effectivePreviewUrl = id && videoData ? `${env.APP_BASE_URL || ''}${videoData.maS3MediaKeyUrl}` : previewUrl;

  return (
    <VideoPreviewSingle
      file={effectiveFile}
      previewUrl={effectivePreviewUrl}
      title={effectiveTitle}
      description={effectiveDescription}
      category={effectiveCategory}
      author={effectiveAuthor}
      item={effectiveItem}
      showDetails={!!id}
      isReel={isReel}
      onClose={() => {
        if (effectiveReturnPath) {
          navigate(effectiveReturnPath);
        } else {
          navigate(-1);
        }
      }}
    />
  );
};

export default VideoPreviewSinglePage;
